import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useMdmSearchActivityMutation } from "../../api/services/mdmActivity/mdmActivityApis";
import { useGetProgramFrameWork } from "../../api/services/programTemplate/programTemplateApis";
import { MuiBox } from "../../components";
import {
  LANGUAGE_CODE,
  MDM_ACTIVITY_SEARCH_TYPE_KEY,
  MDM_ACTIVITY_TYPE_KEY,
  PAGE_IDS,
} from "../../helpers/constants";
import { setApiDataToDropDownOption } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { clearMdmActivityState } from "../../redux/slices/mdmActivity/mdmActivitySlice";
import { MdmAddActivity } from "../mdmAddActivity/MdmAddActivity";
import MdmAddTopic from "../mdmAddTopic/MdmAddTopic";
import { MdmViewActivity } from "../MdmViewActivity/MdmViewActivity";
import { MdmViewTopic } from "../mdmViewTopic/MdmViewTopic";
import { MdmActivitySearchForm } from "./components/mdmActivitySearchForm/MdmActivitySearchForm";
import { MdmActivitySearchHeader } from "./components/mdmActivitySearchHeader/MdmActivitySearchHeader";
import { MdmActivitySearchResults } from "./components/mdmActivitySearchResults/MdmActivitySearchResults";
import {
  activitySearchFormDefaultValues,
  ActivitySearchFormKey,
  ActivitySearchFormType,
  MdmSearchActivityResultRowValue,
} from "./modelsAndSchemas/models";
import { SearchMdmActivitySchema } from "./modelsAndSchemas/ValidationSchema";

export const pageId = PAGE_IDS.MDM_ACTIVITY_SEARCH;

const MdmActivitySearch: FC = () => {
  const [isSearchClick, setIsSearchClick] = useState(false);

  const [activityModalState, setActivityModalState] = useState({
    showAdd: false,
    showView: false,
    selectedActivityId: "",
    showEdit: false,
  });

  const [topicModalState, setTopicModalState] = useState({
    showAdd: false,
    showView: false,
    selectedId: "",
    showEdit: false,
  });

  const { commonValidationMessages, commonLabels } = useSiteCore();
  const dispatch = useDispatch();

  const methods = useForm<ActivitySearchFormType>({
    mode: "all",
    defaultValues: activitySearchFormDefaultValues,
    resolver: yupResolver(SearchMdmActivitySchema(commonValidationMessages)),
  });

  const { reset, getValues } = methods;

  const { data: programFrameWorkData, isError: isProgramFrameWorkError } =
    useGetProgramFrameWork(LANGUAGE_CODE.EN_US, MDM_ACTIVITY_TYPE_KEY.program);

  const { data: projectFrameWorkData, isError: isProjectFrameWorkError } =
    useGetProgramFrameWork(LANGUAGE_CODE.EN_US, MDM_ACTIVITY_TYPE_KEY.project);

  const {
    mutateAsync: getMdmSearchActivityList,
    isPending,
    data: searchResult,
    isError: isSearchActivityError,
  } = useMdmSearchActivityMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  useEffect(() => {
    if (
      isProgramFrameWorkError ||
      isSearchActivityError ||
      isProjectFrameWorkError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProgramFrameWorkError, isSearchActivityError, isProjectFrameWorkError]);

  const handleSearchClick = async () => {
    setIsSearchClick(true);
    getSearchResults();
  };

  const handleClearSearchState = () => {
    removeSavedFilters();
    setIsSearchClick(false);
    getSearchResults();
  };

  const handleClearAllSearch = () => {
    reset(activitySearchFormDefaultValues);
    handleClearSearchState();
  };

  const onRadioToggle = () => {
    const { searchType } = getValues();
    reset({
      ...activitySearchFormDefaultValues,
      [ActivitySearchFormKey.searchType]: searchType,
    });
    handleClearSearchState();
  };

  useEffect(() => {
    getSearchResults();
  }, []);

  const getSearchResults = () => {
    const {
      activityType,
      activityTypeFilter,
      topic,
      topicFilter,
      activityStatus,
      programFramework,
      searchType,
    } = getValues();
    const isSearchTypeAll = searchType === MDM_ACTIVITY_SEARCH_TYPE_KEY.all;

    const params = {
      ...(!isSearchTypeAll && { type: searchType }),
      activityTypeFilter,
      activityType,
      topicFilter,
      topic,
      activityStatus,
      programFrameworkId: programFramework,
    };
    getMdmSearchActivityList(params);
  };

  const removeSavedFilters = () => {
    // TO-DO:
  };

  const onCreateActivity = () => {
    setActivityModalState((prev) => ({
      ...prev,
      showAdd: true,
      showEdit: false,
      selectedActivityId: "",
    }));
    clearActivityState();
  };

  const clearActivityState = () => {
    dispatch(clearMdmActivityState());
  };

  const handleCloseActivityModal = () => {
    setActivityModalState((prev) => ({
      ...prev,
      showAdd: false,
      showEdit: false,
    }));
  };
  const handleCloseAddActivityModal = () => {
    handleCloseActivityModal();
    dispatch(clearMdmActivityState());
  };

  const handleTopicModal = () => {
    // add topic
    setTopicModalState((prev) => ({ ...prev, showAdd: true }));
  };

  const activitySuccessHandler = () => {
    handleCloseActivityModal();
    getSearchResults();
  };

  const handleViewActivity = (activityId: string) => {
    clearActivityState();
    setActivityModalState((prev) => ({
      ...prev,
      showView: true,
      selectedActivityId: activityId,
    }));
  };

  const handleEditActivity = (activityId: string) => {
    clearActivityState();
    setActivityModalState((prev) => ({
      ...prev,
      showEdit: true,
      selectedActivityId: activityId,
    }));
  };

  const handleEditActivityFromView = () => {
    setActivityModalState((prev) => ({
      ...prev,
      showEdit: true,
      showView: false,
    }));
  };

  const handleViewTopic = (topicId: string) => {
    setTopicModalState((prev) => ({
      ...prev,
      showView: true,
      showEdit: false,
      selectedId: topicId,
    }));
  };

  const handleEditTopic = (topicId: string) => {
    setTopicModalState((prev) => ({
      ...prev,
      showAdd: false,
      showView: false,
      showEdit: true,
      selectedId: topicId,
    }));
  };

  const handleEditTopicFromView = () => {
    setTopicModalState((prev) => ({
      ...prev,
      showView: false,
      showEdit: true,
    }));
  };

  const handleCloseTopicModal = () => {
    setTopicModalState((prev) => ({
      ...prev,
      selectedId: "",
      showAdd: false,
      showView: false,
      showEdit: false,
    }));
  };

  const handleTopicViewClose = () => {
    setTopicModalState((prev) => ({
      ...prev,
      showView: false,
      showEdit: false,
      selectedId: "",
    }));
  };

  return (
    <MuiBox data-testid="mdm-activity-search">
      <MdmActivitySearchHeader
        pageId={pageId}
        onCreateActivity={onCreateActivity}
      />
      <FormProvider {...methods}>
        <MdmActivitySearchForm
          programFrameWorkData={programFrameWorkData}
          projectFrameWorkData={projectFrameWorkData}
          handleSearchClick={handleSearchClick}
          handleClearAllSearch={handleClearAllSearch}
          onRadioToggle={onRadioToggle}
          disableActionBtn={false}
          pageId={pageId}
        />
      </FormProvider>
      <MdmActivitySearchResults
        isSearchClick={isSearchClick}
        pageId={pageId}
        rows={(searchResult?.data as MdmSearchActivityResultRowValue[]) || []}
        setSelectedActivityId={handleViewActivity}
        handleEditActivity={handleEditActivity}
      />
      {(activityModalState.showAdd || activityModalState.showEdit) && (
        <MdmAddActivity
          open={activityModalState.showAdd || activityModalState.showEdit}
          onClose={handleCloseAddActivityModal}
          programFrameWorkList={setApiDataToDropDownOption(
            programFrameWorkData,
          )}
          projectFrameWorkList={setApiDataToDropDownOption(
            projectFrameWorkData,
          )}
          handleTopicModal={handleTopicModal}
          activitySuccessHandler={activitySuccessHandler}
          isEditActivity={activityModalState.showEdit}
          selectedActivityId={activityModalState.selectedActivityId}
          handleEditTopic={handleEditTopic}
        />
      )}

      {activityModalState.showView && (
        <MdmViewActivity
          show={activityModalState.showView}
          handleCloseModal={() =>
            setActivityModalState((prev) => ({ ...prev, showView: false }))
          }
          activityId={activityModalState.selectedActivityId}
          handleViewTopic={handleViewTopic}
          editButtonClick={handleEditActivityFromView}
        />
      )}

      {(topicModalState.showAdd || topicModalState.showEdit) && (
        <MdmAddTopic
          show={topicModalState.showAdd || topicModalState.showEdit}
          handleCloseModal={handleCloseTopicModal}
          topicId={topicModalState.selectedId}
          isEditTopic={topicModalState.showEdit}
          isEditActivity={activityModalState.showEdit}
        />
      )}

      {topicModalState.showView && (
        <MdmViewTopic
          show={topicModalState.showView}
          handleCloseModal={handleTopicViewClose}
          editButtonClick={handleEditTopicFromView}
          topicId={topicModalState.selectedId}
        />
      )}
    </MuiBox>
  );
};

export default MdmActivitySearch;
